/**
 * Deterministische Klassifikation einer Rechtschreib-Korrektur (Original -> Korrigiert) in ein
 * kurzes deutsches Label + eine grobe Art (klein/gross). KEINE Modell-Tokens, rein aus dem
 * String-Vergleich. Damit sieht der Betreiber SOFORT, WAS sich geaendert hat ("Komma entfernt",
 * "Bindestrich korrigiert", "Tippfehler korrigiert"), statt den Diff selbst suchen zu muessen.
 *
 * Dieselbe Funktion existiert (bewusst identisch) in apps/agent/src/spell-change.ts — die beiden
 * Pakete werden getrennt gebuendelt, deshalb dupliziert statt ueber Paketgrenzen importiert.
 */
export type SpellChangeKind = "small" | "big";
export interface SpellChange {
  label: string;
  kind: SpellChangeKind;
}

/** Zeichen ohne Kommata und Leerraum — Signatur, um reine Komma-/Leerzeichen-Aenderungen zu erkennen. */
function coreSignature(s: string): string {
  return s.replace(/[\s,]+/g, "");
}
function countChar(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n++;
  return n;
}
/** Ein direkt hintereinander doppelt stehendes Wort wurde entfernt? (z.B. "Burrata Burrata" -> "Burrata") */
function isDuplicateWordRemoved(a: string, b: string): boolean {
  const wa = a.trim().split(/\s+/).filter(Boolean);
  const wb = b.trim().split(/\s+/).filter(Boolean);
  if (wa.length !== wb.length + 1) return false;
  for (let i = 0; i + 1 < wa.length; i++) {
    if (wa[i] === wa[i + 1]) {
      const candidate = [...wa.slice(0, i), ...wa.slice(i + 1)];
      if (candidate.join(" ") === wb.join(" ")) return true;
    }
  }
  return false;
}

/**
 * Vergleicht Original und Korrektur und liefert ein knappes deutsches Label + die Art:
 * "small" = Kleinigkeit (Komma, Leerzeichen, Bindestrich, doppeltes Wort, Gross-/Kleinschreibung),
 * "big"   = echte Buchstabenaenderung innerhalb eines Wortes (Tippfehler).
 */
export function describeSpellChange(original: string, corrected: string): SpellChange {
  const a = original ?? "";
  const b = corrected ?? "";
  if (a === b) return { label: "Keine Änderung", kind: "small" };

  // Nur Kommata/Leerzeichen unterscheiden sich (die Buchstaben sind identisch).
  if (coreSignature(a) === coreSignature(b)) {
    const commasA = countChar(a, ",");
    const commasB = countChar(b, ",");
    if (commasB > commasA) return { label: "Komma ergänzt", kind: "small" };
    if (commasB < commasA) return { label: "Komma entfernt", kind: "small" };
    return { label: "Leerzeichen korrigiert", kind: "small" };
  }

  // Bindestriche kommen hinzu oder fallen weg.
  if (countChar(a, "-") !== countChar(b, "-")) {
    return { label: "Bindestrich korrigiert", kind: "small" };
  }

  // Ein doppeltes Wort direkt hintereinander wurde entfernt.
  if (isDuplicateWordRemoved(a, b)) {
    return { label: "Doppeltes Wort entfernt", kind: "small" };
  }

  // Nur die Gross-/Kleinschreibung unterscheidet sich.
  if (a.toLowerCase() === b.toLowerCase()) {
    return { label: "Groß-/Kleinschreibung", kind: "small" };
  }

  // Sonst: echte Buchstabenaenderung innerhalb eines Wortes.
  return { label: "Tippfehler korrigiert", kind: "big" };
}

/**
 * Kompakte geaenderte Wortspanne (gemeinsamen Praefix/Suffix abschneiden) — fuer eine knappe
 * Anzeige "Masacarpone → Mascarpone" statt des ganzen Satzes. Faellt auf das volle Original/Korrektur
 * zurueck, falls sich keine saubere Wortspanne bestimmen laesst.
 */
export function changedSpan(original: string, corrected: string): { from: string; to: string } {
  const a = (original ?? "").trim().split(/\s+/).filter(Boolean);
  const b = (corrected ?? "").trim().split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  let j = 0;
  while (j < a.length - i && j < b.length - i && a[a.length - 1 - j] === b[b.length - 1 - j]) j++;
  const from = a.slice(i, a.length - j).join(" ");
  const to = b.slice(i, b.length - j).join(" ");
  return { from: from || (original ?? ""), to: to || (corrected ?? "") };
}
